import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import {
  Loader2,
  FileCheck,
  Check,
  X,
  FileText,
  Send,
  Download,
  Plus,
  Info,
  CheckCircle2,
} from 'lucide-react';
import { formatDate } from '@/lib/utils';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

// Type definitions
interface CompletionOfWorksRecord {
  id: number;
  customerId: number | null;
  installationId: number | null;
  customerName: string | null;
  customerAddress: string | null;
  projectRefNumber: string | null;
  installationAddress: string | null;
  installationDescription: string | null;
  confirmationBuildingRegs: boolean;
  manufacturersManuals: boolean;
  serviceRequirements: boolean;
  maintenanceRequirements: boolean;
  performanceEstimates: boolean;
  mcsCertificate: boolean;
  mcsComplianceCertificate: boolean;
  customerComments: string | null;
  signatureImage: string | null;
  status: string | null;
  accessToken: string | null;
  dateOfSigning: string | null;
  sentAt: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

interface CompletionOfWorksProps {
  installationId: number;
  installation: any;
  customer: any;
}

// Schema for the form
const completionOfWorksSchema = z.object({
  customerName: z.string().min(1, { message: 'Customer name is required' }),
  customerAddress: z.string().min(1, { message: 'Customer address is required' }),
  projectRefNumber: z.string().min(1, { message: 'Project reference number is required' }),
  installationAddress: z.string().min(1, { message: 'Installation address is required' }),
  installationDescription: z.string().min(1, { message: 'Description is required' }),
  // Default all checkboxes to true since they're pre-checked and not shown on the form
  confirmationBuildingRegs: z.boolean().default(true),
  manufacturersManuals: z.boolean().default(true),
  serviceRequirements: z.boolean().default(true),
  maintenanceRequirements: z.boolean().default(true),
  performanceEstimates: z.boolean().default(true),
  mcsCertificate: z.boolean().default(true),
  mcsComplianceCertificate: z.boolean().default(true),
  customerEmail: z.string().email({ message: 'Valid email is required' }),
  customerPhone: z.string().optional(),
});

export function CompletionOfWorksRecords({
  installationId,
  installation,
  customer,
}: CompletionOfWorksProps) {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch completion of works records
  const {
    data: records = [],
    isLoading,
    isError,
    error,
  } = useQuery<CompletionOfWorksRecord[]>({
    queryKey: ['/api/completion-of-works', installationId],
    queryFn: async () => {
      try {
        console.log(`Fetching completion records for installation ID: ${installationId}`);
        // The issue is with the apiRequest function - it requires the first arg to be the URL and second arg to include method
        const url = `/api/completion-of-works?installationId=${installationId}`;
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`API request failed with status ${response.status}`);
        }

        const data = await response.json();
        console.log('Response data:', data);
        return data || [];
      } catch (error) {
        console.error('Error fetching completion records:', error);
        throw error;
      }
    },
    retry: 2,
  });

  // Create record mutation
  const createRecordMutation = useMutation({
    mutationFn: async (values: z.infer<typeof completionOfWorksSchema>) => {
      const response = await fetch('/api/completion-of-works', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...values,
          installationId: installationId,
          customerId: installation.customerId,
          status: 'draft',
        }),
      });

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      return response.json();
    },
    onSuccess: () => {
      // Update the query key to match our specific installation ID filter
      queryClient.invalidateQueries({ queryKey: ['/api/completion-of-works', installationId] });
      toast({
        title: 'Success',
        description: 'Completion of Works Record created successfully.',
      });
      setIsCreateDialogOpen(false);
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'Failed to create Completion of Works Record. Please try again.',
        variant: 'destructive',
      });
      console.error('Create record error:', error);
    },
  });

  // Send record for signature mutation
  const sendRecordMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await fetch(`/api/completion-of-works/${id}/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/completion-of-works', installationId] });
      toast({
        title: 'Success',
        description: 'Completion of Works Record sent for signature.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'Failed to send Completion of Works Record. Please try again.',
        variant: 'destructive',
      });
      console.error('Send record error:', error);
    },
  });

  const form = useForm<z.infer<typeof completionOfWorksSchema>>({
    resolver: zodResolver(completionOfWorksSchema),
    defaultValues: {
      customerName: customer?.name || '',
      customerAddress: customer?.address || '',
      projectRefNumber: installation?.projectReference || '',
      installationAddress: installation?.installationAddress || '',
      installationDescription: installation?.systemType || 'Solar PV Installation',
      confirmationBuildingRegs: true, // Auto-check all checkboxes
      manufacturersManuals: true,
      serviceRequirements: true,
      maintenanceRequirements: true,
      performanceEstimates: true,
      mcsCertificate: true,
      mcsComplianceCertificate: true,
      customerEmail: customer?.email || '',
      customerPhone: customer?.phone || '',
    },
  });

  const onSubmit = (values: z.infer<typeof completionOfWorksSchema>) => {
    createRecordMutation.mutate(values);
  };

  // Handle sending a record for signature
  const handleSendForSignature = (id: number) => {
    sendRecordMutation.mutate(id);
  };

  // Get status badge
  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case 'draft':
        return <Badge variant="outline">Draft</Badge>;
      case 'sent':
        return <Badge variant="secondary">Sent for Signing</Badge>;
      case 'signed':
        return <Badge variant="success">Signed</Badge>;
      case 'cancelled':
        return <Badge variant="destructive">Cancelled</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">Loading Completion of Works Records...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="py-8 text-center">
        <X className="h-12 w-12 text-destructive mx-auto mb-4" />
        <p className="text-muted-foreground">Error loading records. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <FileCheck className="h-5 w-5 text-muted-foreground" />
          Completion of Works Records
        </h2>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              Create New Record
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl overflow-y-auto max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>Create Completion of Works Record</DialogTitle>
              <DialogDescription>
                Fill in the details below to create a new Completion of Works Record.
              </DialogDescription>
            </DialogHeader>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <FormField
                    control={form.control}
                    name="customerName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Customer Name</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="customerEmail"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Customer Email</FormLabel>
                        <FormControl>
                          <Input {...field} type="email" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="customerAddress"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Customer Address</FormLabel>
                      <FormControl>
                        <Textarea {...field} rows={3} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <FormField
                    control={form.control}
                    name="projectRefNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Project Reference Number</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="customerPhone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Customer Phone (Optional)</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="installationAddress"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Installation Address</FormLabel>
                      <FormControl>
                        <Textarea {...field} rows={3} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="installationDescription"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Installation Description</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="border rounded-md p-5 bg-muted/20 shadow-sm">
                  <div className="flex items-center mb-3">
                    <div className="bg-primary/10 p-2 rounded-full mr-3">
                      <Info className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="font-medium text-base">Documentation Provided</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4 pl-1">
                    The customer will be asked to confirm receipt of the following documentation:
                  </p>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <li className="flex items-start gap-2 p-2 rounded-md hover:bg-muted/30 transition-colors">
                      <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>Building Regulations Compliance Certification</span>
                    </li>
                    <li className="flex items-start gap-2 p-2 rounded-md hover:bg-muted/30 transition-colors">
                      <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>Manufacturer's Installation and Operating Manuals</span>
                    </li>
                    <li className="flex items-start gap-2 p-2 rounded-md hover:bg-muted/30 transition-colors">
                      <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>Service Requirements</span>
                    </li>
                    <li className="flex items-start gap-2 p-2 rounded-md hover:bg-muted/30 transition-colors">
                      <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>Maintenance Requirements</span>
                    </li>
                    <li className="flex items-start gap-2 p-2 rounded-md hover:bg-muted/30 transition-colors">
                      <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>Performance and Financial Estimates</span>
                    </li>
                    <li className="flex items-start gap-2 p-2 rounded-md hover:bg-muted/30 transition-colors">
                      <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>MCS or Flexi-Orb Certificate</span>
                    </li>
                    <li className="flex items-start gap-2 p-2 rounded-md hover:bg-muted/30 transition-colors">
                      <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>MCS Compliance Certificate</span>
                    </li>
                  </ul>
                  <p className="text-xs text-muted-foreground mt-4 italic">
                    These items will be automatically checked in the document. The customer will
                    need to confirm receipt by signature.
                  </p>
                </div>

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsCreateDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createRecordMutation.isPending}>
                    {createRecordMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Create Record'
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      {records.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center">
            <div className="mb-4 flex justify-center">
              <FileCheck className="h-12 w-12 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium">No Completion of Works Records Found</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Create a new record to document that the customer has received all necessary
              documentation and is satisfied with the completed installation work.
            </p>
            <Button className="mt-4 gap-2" onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4" />
              Create First Record
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Last Updated</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.map((record) => (
                <TableRow key={record.id}>
                  <TableCell className="font-medium">{record.id}</TableCell>
                  <TableCell>{record.customerName}</TableCell>
                  <TableCell>{getStatusBadge(record.status)}</TableCell>
                  <TableCell>{record.createdAt ? formatDate(record.createdAt) : 'N/A'}</TableCell>
                  <TableCell>{record.updatedAt ? formatDate(record.updatedAt) : 'N/A'}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {record.status === 'draft' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSendForSignature(record.id)}
                          disabled={sendRecordMutation.isPending}
                          className="flex items-center gap-1"
                        >
                          {sendRecordMutation.isPending ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Send className="h-3 w-3" />
                          )}
                          <span>Send</span>
                        </Button>
                      )}
                      {record.status === 'signed' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            window.open(`/api/completion-of-works/${record.id}/pdf`, '_blank')
                          }
                          className="flex items-center gap-1"
                        >
                          <Download className="h-3 w-3" />
                          <span>PDF</span>
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => window.open(`/completion-of-works/${record.id}`, '_blank')}
                        className="flex items-center gap-1"
                      >
                        <FileText className="h-3 w-3" />
                        <span>View</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
